import React, { useState, useCallback } from 'react';
import { Copy, Download, RefreshCw, FileText } from 'lucide-react';

interface SqlFormatterProps {}

const SqlFormatter: React.FC<SqlFormatterProps> = () => {
  const [inputSql, setInputSql] = useState<string>('');
  const [formattedSql, setFormattedSql] = useState<string>('');
  const [indentSize, setIndentSize] = useState<number>(2);
  const [isFormatting, setIsFormatting] = useState<boolean>(false);

  const sqlKeywords = [
    'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN',
    'ON', 'AND', 'OR', 'ORDER BY', 'GROUP BY', 'HAVING', 'INSERT', 'UPDATE', 'DELETE',
    'CREATE', 'ALTER', 'DROP', 'TABLE', 'INDEX', 'VIEW', 'DATABASE', 'SCHEMA',
    'PRIMARY KEY', 'FOREIGN KEY', 'REFERENCES', 'CONSTRAINT', 'UNIQUE', 'NOT NULL',
    'DEFAULT', 'AUTO_INCREMENT', 'LIMIT', 'OFFSET', 'UNION', 'CASE', 'WHEN', 'THEN',
    'ELSE', 'END', 'AS', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'LIKE',
    'IN', 'EXISTS', 'BETWEEN', 'IS', 'NULL', 'TRUE', 'FALSE'
  ];

  const formatSql = useCallback((sql: string): string => {
    if (!sql.trim()) return '';

    let formatted = sql;
    const indent = ' '.repeat(indentSize);
    
    // Remove extra whitespace and normalize
    formatted = formatted.replace(/\s+/g, ' ').trim();
    
    // Add line breaks before major keywords
    const majorKeywords = ['SELECT', 'FROM', 'WHERE', 'ORDER BY', 'GROUP BY', 'HAVING', 'UNION'];
    majorKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      formatted = formatted.replace(regex, `\n${keyword}`);
    });
    
    // Handle JOIN statements
    const joinKeywords = ['JOIN', 'INNER JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'FULL JOIN'];
    joinKeywords.forEach(join => {
      const regex = new RegExp(`\\b${join}\\b`, 'gi');
      formatted = formatted.replace(regex, `\n${indent}${join}`);
    });
    
    // Handle ON clauses
    formatted = formatted.replace(/\bON\b/gi, `\n${indent}${indent}ON`);
    
    // Handle AND/OR in WHERE and ON clauses
    formatted = formatted.replace(/\b(AND|OR)\b/gi, `\n${indent}${indent}$1`);
    
    // Handle commas in SELECT statements
    formatted = formatted.replace(/,\s*(?![^()]*\))/g, `,\n${indent}`);
    
    // Handle parentheses
    formatted = formatted.replace(/\(\s*/g, '(\n' + indent);
    formatted = formatted.replace(/\s*\)/g, '\n)');
    
    // Clean up multiple line breaks
    formatted = formatted.replace(/\n\s*\n/g, '\n');
    
    // Trim each line and remove empty lines
    const lines = formatted.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    
    return lines.join('\n');
  }, [indentSize]);

  const handleFormat = useCallback(async () => {
    if (!inputSql.trim()) return;
    
    setIsFormatting(true);
    
    // Simulate formatting delay for better UX
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const formatted = formatSql(inputSql);
    setFormattedSql(formatted);
    setIsFormatting(false);
  }, [inputSql, formatSql]);

  const handleCopy = useCallback(async () => {
    if (!formattedSql) return;
    
    try {
      await navigator.clipboard.writeText(formattedSql);
      // Simple feedback - in a real app you'd want a toast notification
      const button = document.getElementById('copy-button');
      if (button) {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        setTimeout(() => {
          button.textContent = originalText;
        }, 1500);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, [formattedSql]);

  const handleDownload = useCallback(() => {
    if (!formattedSql) return;
    
    const blob = new Blob([formattedSql], { type: 'text/sql' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'formatted-query.sql';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [formattedSql]);

  const handleClear = useCallback(() => {
    setInputSql('');
    setFormattedSql('');
  }, []);

  const highlightSql = useCallback((sql: string): string => {
    if (!sql) return '';
    
    let highlighted = sql;
    
    // Highlight SQL keywords
    sqlKeywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      highlighted = highlighted.replace(regex, `<span class="text-blue-600 font-semibold">${keyword.toUpperCase()}</span>`);
    });
    
    // Highlight strings
    highlighted = highlighted.replace(/'([^']*)'/g, '<span class="text-green-600">\'$1\'</span>');
    highlighted = highlighted.replace(/"([^"]*)"/g, '<span class="text-green-600">"$1"</span>');
    
    // Highlight numbers
    highlighted = highlighted.replace(/\b\d+(\.\d+)?\b/g, '<span class="text-purple-600">$&</span>');
    
    // Highlight comments
    highlighted = highlighted.replace(/--.*$/gm, '<span class="text-gray-500 italic">$&</span>');
    highlighted = highlighted.replace(/\/\*[\s\S]*?\*\//g, '<span class="text-gray-500 italic">$&</span>');
    
    return highlighted;
  }, []);

  const exampleQuery = `SELECT u.id, u.name, u.email, COUNT(o.id) as order_count FROM users u LEFT JOIN orders o ON u.id = o.user_id WHERE u.created_at >= '2023-01-01' AND u.status = 'active' GROUP BY u.id, u.name, u.email HAVING COUNT(o.id) > 0 ORDER BY order_count DESC, u.name ASC LIMIT 50;`;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div className="flex items-center space-x-3">
              <FileText className="w-8 h-8 text-white" />
              <div>
                <h1 className="text-2xl font-bold text-white">SQL Formatter</h1>
                <p className="text-blue-100">Format and beautify your SQL queries</p>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="indent-size" className="text-sm font-medium text-gray-700">
                  Indent Size:
                </label>
                <select
                  id="indent-size"
                  value={indentSize}
                  onChange={(e) => setIndentSize(Number(e.target.value))}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={2}>2 spaces</option>
                  <option value={4}>4 spaces</option>
                  <option value={8}>8 spaces</option>
                </select>
              </div>
              
              <button
                onClick={() => setInputSql(exampleQuery)}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md transition-colors duration-200"
              >
                Load Example
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 h-96 lg:h-[600px]">
            {/* Input Panel */}
            <div className="flex flex-col border-r border-gray-200">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Input SQL</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleClear}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors duration-200"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleFormat}
                    disabled={isFormatting || !inputSql.trim()}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white text-sm rounded transition-colors duration-200"
                  >
                    {isFormatting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                    <span>{isFormatting ? 'Formatting...' : 'Format'}</span>
                  </button>
                </div>
              </div>
              <textarea
                value={inputSql}
                onChange={(e) => setInputSql(e.target.value)}
                placeholder="Paste your SQL query here..."
                className="flex-1 p-4 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                spellCheck={false}
              />
            </div>

            {/* Output Panel */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-700">Formatted SQL</h2>
                <div className="flex space-x-2">
                  <button
                    id="copy-button"
                    onClick={handleCopy}
                    disabled={!formattedSql}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </button>
                  <button
                    onClick={handleDownload}
                    disabled={!formattedSql}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent rounded transition-colors duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div className="flex-1 p-4 bg-gray-900 overflow-auto">
                {formattedSql ? (
                  <pre
                    className="font-mono text-sm text-gray-100 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: highlightSql(formattedSql)
                    }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Formatted SQL will appear here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <span>Characters: {inputSql.length}</span>
                <span>Lines: {inputSql.split('\n').length}</span>
              </div>
              <div className="text-xs">
                Supports: SELECT, INSERT, UPDATE, DELETE, CREATE, ALTER, DROP queries
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SqlFormatter;